<script setup lang="tsx">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Autocomplete from '@/components/widgets/Autocomplete.vue'
import { getMethodStatusText, getTypeIcon, getTypeText, viewing } from '@/services/utils'
import { TagAsset, TagBP } from '@/models/enums-generated'
import type {
	MethodImportUnmatchedMethod as UnmatchedMethod,
	MethodImportNameMismatch as NameMismatch,
	MethodImportSuggestedItem as SuggestedItem,
} from '@/models/api-generated'
import {
	statusIcon,
	importResponsibleIcon,
	fmtDate,
	effectiveStatus as _effectiveStatus,
	isDoNotImport as _isDoNotImport,
	hasStatusMismatch,
} from './importUtils'

const props = defineProps<{
	mod: { groupKey: string; moduleId: number; moduleName: string; moduleType: string }
	methods: UnmatchedMethod[]
	importableMethods: UnmatchedMethod[]
	selectedIds: Set<number>
	isExpanded: boolean
	asset: SuggestedItem | undefined
	bps: SuggestedItem[]
	bpInput: SuggestedItem | null
	excludedAssetIds: Set<number>
	siblingAssetIds: Set<number>
	restoreInfo: { isRestore: boolean; restoreAssetName: string; totalModuleMethods: number } | undefined
	isSiblingDuplicate: boolean
	isExcludedSelected: boolean
	mismatchByName: Record<string, NameMismatch>
	statusReplacementMap: Record<string, string>
	approvedVersionMismatch: boolean
}>()

const emit = defineEmits<{
	toggle: []
	'update:asset': [val: SuggestedItem | undefined]
	'add-bp': [val: SuggestedItem | null]
	'remove-bp': [idx: number]
}>()

const { t } = useI18n()

const effectiveStatus = (s: string) => _effectiveStatus(s, props.statusReplacementMap)
const isDoNotImport = (s: string) => _isDoNotImport(s, props.statusReplacementMap)

const isService = computed(() => props.mod.moduleType === 'S')
const hasAnySelected = computed(() => props.methods.some((m) => props.selectedIds.has(m.methodId)))
const isGroupChecked = computed(
	() => props.importableMethods.length > 0 && props.importableMethods.every((m) => props.selectedIds.has(m.methodId)),
)
const selectedInGroup = computed(() => props.methods.filter((m) => props.selectedIds.has(m.methodId)).length)
const isAllMethodsChecked = computed(() => props.methods.every((m) => props.selectedIds.has(m.methodId)))
const secWarnSelected = computed(() => props.methods.some((m) => m.highSecNeedWarning && props.selectedIds.has(m.methodId)))
const versionMismatchSelected = computed(
	() => !props.approvedVersionMismatch && props.methods.some((m) => props.mismatchByName[m.name] && props.selectedIds.has(m.methodId)),
)
const statusMismatchSelected = computed(() =>
	props.methods.some(
		(m) => hasStatusMismatch(m.imported.status) && !props.statusReplacementMap[m.imported.status] && props.selectedIds.has(m.methodId),
	),
)
const responsibleIssueCount = computed(
	() => props.methods.filter((m) => m.imported.responsibleLabel && !m.imported.responsibleId && props.selectedIds.has(m.methodId)).length,
)

function toggleGroup(e: Event) {
	const checked = (e.target as HTMLInputElement).checked
	for (const m of props.importableMethods) {
		if (checked) props.selectedIds.add(m.methodId)
		else props.selectedIds.delete(m.methodId)
	}
}

function toggleAll(e: Event) {
	const checked = (e.target as HTMLInputElement).checked
	for (const m of props.methods) {
		if (checked) props.selectedIds.add(m.methodId)
		else props.selectedIds.delete(m.methodId)
	}
}

function toggleMethod(e: Event, id: number) {
	if ((e.target as HTMLInputElement).checked) props.selectedIds.add(id)
	else props.selectedIds.delete(id)
}
</script>

<template>
	<div class="card mb-3">
		<div class="card-header d-flex flex-wrap align-items-center gap-2 cursor-pointer" @click="emit('toggle')">
			<input type="checkbox" class="form-check-input flex-shrink-0" :checked="isGroupChecked" @click.stop @change="toggleGroup" />
			<div class="d-flex align-items-center gap-1">
				<i class="bi bi-layers text-muted"></i>
				<span class="fw-semibold">{{ mod.moduleName }}</span>
				<span v-if="methods[0]?.moduleTitle" class="text-muted small">— {{ methods[0].moduleTitle }}</span>
			</div>

			<span v-if="restoreInfo?.isRestore" class="badge bg-warning text-dark">
				<i class="bi bi-arrow-counterclockwise me-1"></i>{{ t('import.restoringConnection') }}
			</span>
			<span
				v-if="isService && methods[0]?.importedAssetName"
				class="badge bg-warning-subtle border border-warning-subtle text-warning-emphasis fw-normal"
			>
				<i class="bi bi-exclamation-triangle me-1"></i>
				{{ t('import.fileAssetNotInSystem', { name: methods[0].importedAssetName }) }}
			</span>
			<span class="badge bg-primary-subtle border border-primary-subtle text-primary fw-normal">
				{{ selectedInGroup }}/{{ methods.length }}
			</span>

			<div class="d-flex align-items-center gap-2 ms-auto">
				<span v-if="isService && !asset?.id" class="badge bg-danger">
					<i class="bi bi-clipboard me-1"></i>{{ t('import.assetMissing') }}
				</span>
				<span v-if="!bps.length" class="badge bg-danger"> <i class="bi bi-kanban me-1"></i>{{ t('import.bpMissing') }} </span>
				<span v-if="secWarnSelected" class="badge bg-warning text-dark">
					<i class="bi bi-exclamation-triangle me-1"></i>{{ t('method.securityType') }}
				</span>
				<span v-if="versionMismatchSelected" class="badge bg-warning text-dark">
					<i class="bi bi-file-earmark-diff me-1"></i>{{ t('import.versionMismatch') }}
				</span>
				<span v-if="statusMismatchSelected" class="badge bg-danger">
					<i class="bi bi-arrow-repeat me-1"></i>{{ t('import.statusMismatch') }}
				</span>
				<span v-if="responsibleIssueCount > 0" class="badge bg-danger">
					<i class="bi bi-person-x me-1"></i>{{ responsibleIssueCount }}
				</span>
				<i :class="isExpanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="text-muted"></i>
			</div>
		</div>
		<template v-if="isExpanded">
			<div class="card-body">
				<div v-if="restoreInfo?.isRestore" class="alert alert-warning py-2 px-2 small mb-2 d-flex align-items-center gap-2">
					<i class="bi bi-arrow-counterclockwise flex-shrink-0"></i>
					<span>
						{{ t('import.restoringConnection') }}: <strong>{{ restoreInfo.restoreAssetName }}</strong> —
						{{ t('import.connectionPreviouslyExisted') }}
					</span>
				</div>

				<div
					v-if="isService && asset?.id && bps.length && restoreInfo?.totalModuleMethods"
					class="alert alert-info py-1 px-2 small mb-2 d-flex align-items-center gap-1"
				>
					<i class="bi bi-info-circle-fill flex-shrink-0"></i>
					<span>
						{{ mod.moduleName }} — <strong>{{ asset.name }}</strong
						>: {{ restoreInfo.totalModuleMethods }} {{ t('method.label', restoreInfo.totalModuleMethods).toLowerCase() }}
						{{ t('import.measuresWillBeEnabled') }}
					</span>
				</div>

				<div
					v-if="isService && !asset?.id && hasAnySelected"
					class="alert alert-warning py-1 px-2 small mb-2 d-flex align-items-center gap-1"
				>
					<i class="bi bi-exclamation-triangle-fill"></i>
					{{ t('misc.select') }} {{ t('asset.label').toLowerCase() }} — {{ t('misc.nothing') }}
				</div>

				<div v-if="isService && isSiblingDuplicate" class="alert alert-danger py-1 px-2 small mb-2 d-flex align-items-center gap-1">
					<i class="bi bi-x-circle-fill"></i>{{ t('import.assetDuplicateInModule') }}
				</div>
				<div v-else-if="isService && isExcludedSelected" class="alert alert-danger py-1 px-2 small mb-2 d-flex align-items-center gap-1">
					<i class="bi bi-x-circle-fill"></i>{{ t('import.assetAlreadyConnected') }}
				</div>

				<div
					v-if="isService && !bps.length && hasAnySelected"
					class="alert alert-warning py-1 px-2 small mb-2 d-flex align-items-center gap-1"
				>
					<i class="bi bi-exclamation-triangle-fill"></i>
					{{ t('misc.select') }} {{ t('businessProcess').toLowerCase() }} — {{ t('misc.nothing') }}
				</div>

				<div class="row g-2 mb-3">
					<div v-if="isService" class="col-6">
						<label class="form-label small mb-1">{{ t('asset.label') }} *</label>
						<Autocomplete
							url="/api/assetconnections"
							:type="TagAsset"
							:instance-id="viewing"
							:valid-input="true"
							:label="t('asset.label')"
							:exlude-ids="[...excludedAssetIds, ...siblingAssetIds]"
							:model-value="asset"
							@update:model-value="(val) => emit('update:asset', val)"
						/>
					</div>
					<div :class="isService ? 'col-6' : 'col-12'">
						<label class="form-label small mb-1">{{ t('businessProcess') }}<template v-if="isService"> *</template></label>
						<div v-if="bps.length" class="d-flex flex-wrap gap-1 mb-1">
							<span v-for="(bp, i) in bps" :key="bp.id" class="badge bg-primary d-inline-flex align-items-center gap-1">
								{{ bp.name }}
								<button type="button" class="btn-close btn-close-white ms-1 bp-remove" @click="emit('remove-bp', i)"></button>
							</span>
						</div>
						<Autocomplete
							url="/api/assetconnections"
							:type="TagBP"
							:instance-id="viewing"
							:valid-input="true"
							:label="t('businessProcess')"
							:exlude-ids="bps.map((b) => b.id)"
							:model-value="bpInput"
							@update:model-value="(val) => emit('add-bp', val)"
						/>
					</div>
				</div>

				<table class="table table-sm table-hover align-middle mb-0">
					<thead class="table-light">
						<tr>
							<th style="width: 1px">
								<input type="checkbox" class="form-check-input" :checked="isAllMethodsChecked" @change="toggleAll" />
							</th>
							<th>{{ t('method.reference') }}</th>
							<th>{{ t('method.securityType') }}</th>
							<th>{{ t('method.status', 1) }}</th>
							<th>{{ t('responsible') }}</th>
							<th>{{ t('date.dueDate') }}</th>
							<th class="col-2">{{ t('misc.comment') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="m in methods" :key="m.methodId" :class="{ 'table-warning': m.highSecNeedWarning }">
							<td>
								<input
									type="checkbox"
									class="form-check-input"
									:checked="selectedIds.has(m.methodId)"
									:disabled="isDoNotImport(m.imported.status)"
									@change="(e) => toggleMethod(e, m.methodId)"
								/>
							</td>
							<td class="fw-semibold">
								{{ m.name }}
								<span v-if="m.highSecNeedWarning" class="badge bg-warning text-dark ms-1 fw-normal small" :title="t('method.securityType')">
									<i class="bi bi-exclamation-triangle"></i>
								</span>
								<span
									v-if="!approvedVersionMismatch && mismatchByName[m.name]"
									class="badge bg-warning text-dark ms-1 fw-normal small"
									:title="t('import.versionMismatch')"
								>
									<i class="bi bi-file-earmark-diff"></i>
								</span>
								<span
									v-if="hasStatusMismatch(m.imported.status) && !statusReplacementMap[m.imported.status]"
									class="badge bg-danger ms-1 fw-normal small"
									:title="t('import.statusMismatch') + ': ' + m.imported.status"
								>
									<i class="bi bi-arrow-repeat"></i>
								</span>
								<span
									v-if="m.imported.responsibleLabel && !m.imported.responsibleId"
									class="badge bg-danger ms-1 fw-normal small"
									:title="t('import.responsibleNotFound') + ': ' + m.imported.responsibleLabel"
								>
									<i class="bi bi-person-x"></i>
								</span>
								<div v-if="m.methodTitle" class="text-muted small fw-normal">{{ m.methodTitle }}</div>
								<div v-if="!approvedVersionMismatch && mismatchByName[m.name]" class="version-mismatch-detail">
									{{ mismatchByName[m.name].inFile }} → {{ mismatchByName[m.name].inDB }}
								</div>
							</td>
							<td>
								<i :class="getTypeIcon(m.methodType)" :title="getTypeText(m.methodType)"></i>
							</td>
							<td class="text-muted small">
								<i :class="statusIcon(effectiveStatus(m.imported.status))" class="me-1"></i
								>{{ getMethodStatusText(effectiveStatus(m.imported.status)) || effectiveStatus(m.imported.status) || '—' }}
							</td>
							<td>
								<span v-if="m.imported.responsibleLabel && !m.imported.responsibleId" class="text-danger small">
									<i class="bi bi-person-x me-1"></i>{{ m.imported.responsibleLabel }}
								</span>
								<span v-else class="text-muted small">
									<i :class="'bi ' + importResponsibleIcon(m.imported.responsibleType)" class="me-1"></i
									>{{ m.imported.responsibleLabel || '—' }}
								</span>
							</td>
							<td class="text-muted small">{{ fmtDate(m.imported.dueDate) }}</td>
							<td class="text-muted small text-truncate">{{ m.imported.comment || '—' }}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</template>
	</div>
</template>

<style lang="sass" scoped>
.bp-remove
	font-size: 0.55em
</style>
